import React, { useEffect, useState, useRef, useCallback } from "react"
import AuthenticationApi from "Api/AuthenticationApi"
import { useNavigate } from "react-router-dom"
import {
  Col,
  Row,
  Table,
  Input,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { OverlayPanel } from "primereact/overlaypanel"
import { StandardPagination, StatusBadge, ActionIcon } from "components/Common"
import "./Under18Users.scss"

const Under18Users = () => {
  document.title = "Under 18 Users"
  const navigate = useNavigate()
  const accessToken = localStorage.getItem("accessToken")
  const userData = JSON.parse(localStorage.getItem("userData"))
  const permissions = userData?.permission || []
  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit] = useState(10)
  const [usersData, setUsersData] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const op = useRef(null)
  const toast = useRef(null)

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Users", link: "/user-list" },
    { label: "Under 18 Users", link: "#" },
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const getAllUnder18Users = useCallback(() => {
    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    AuthenticationApi.getTeenUsers(config)
      .then(response => {
        setLoading(false)
        setUsersData(response.data.data?.data || [])
        setTotalRecords(response.data.data?.totalTeenUsers || 0)
      })
      .catch(error => {
        console.error("Error fetching Teen Users:", error)
        setLoading(false)
        setUsersData([])
      })
  }, [accessToken, limit, page])


  useEffect(() => {
    getAllUnder18Users()
  }, [getAllUnder18Users])

  const filteredUserDetails = Array.isArray(usersData)
    ? usersData.filter(user => {
      // Filter by status
      const statusMatch = filterStatus === "all" || user.userType === filterStatus

      // Filter by search term
      const searchMatch = !searchTerm ||
        user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase())

      return statusMatch && searchMatch
    })
    : []
  const totalPages = Math.ceil(totalRecords / limit)

  const capitalizeString = str => {
    return str
      .split(" ")
      .map(word => word.slice(0, 1).toLocaleUpperCase() + word.slice(1))
      .join(" ")
  }

  const hasPermission = (permissions, resource, method) => {
    if (!Array.isArray(permissions.permission)) {
      console.error("Permissions is not an array:", permissions)
      return false
    }

    return permissions.permission.some(
      perm =>
        perm.permissionName === resource &&
        perm.permissionType.includes(method),
    )
  }

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      <Row>
        <Col xs="12">
          <div className="user-list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb
              data={breadcrumbItems}
              pageTitle="Under 18 Users"
              handleClick={handleBreadcrumbClick}
            />

            <div className="d-flex align-items-center gap-3">
              <div className="search-container">
                <Input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <i className="mdi mdi-magnify search-icon"></i>
              </div>
            </div>
          </div>
          {(userData.role === "admin" ||
            hasPermission(permissions, "appUsers", "GET")) && (
              <div className="data-table-container">
                <div className="table-responsive mt-4 mb-3">
                  {loading ? (
                    <DataTableLoader cols={5} />
                  ) : (
                    <Table className="table mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>User Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>
                            <span className="d-flex align-items-center gap-2 justify-content-center">
                              <span>User Type</span>
                              <span
                                onClick={e => op.current.toggle(e)}
                                style={{ cursor: "pointer" }}
                              >
                                <i className="mdi mdi-filter" />
                              </span>
                            </span>
                            <OverlayPanel ref={op}>
                              <Input
                                type="select"
                                value={filterStatus}
                                onChange={e => {
                                  setFilterStatus(e.target.value)
                                  op.current.hide()
                                }}
                                style={{ width: "150px" }}
                              >
                                <option value="all">All</option>
                                <option value="student">Student</option>
                                <option value="creator">Creator</option>
                                <option value="">N/A</option>
                              </Input>
                            </OverlayPanel>
                          </th>
                          <th>Status</th>
                          <th>Age</th>
                          {(userData.role === "admin" ||
                            hasPermission(permissions, "appUsers", "GET")) && (
                              <th>View</th>
                            )}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUserDetails?.map((user, index) => (
                          <tr key={user._id} className="user-table-row">
                            <th scope="row">{(page - 1) * limit + index + 1}</th>
                            <td>{user.userName}</td>
                            <td>{user.email}</td>
                            <td>{user.phoneNumber || "N/A"}</td>
                            <td className="text-center">
                              <span>
                                {capitalizeString(user.userType || "N/A")}
                              </span>
                            </td>
                            <td>
                              <StatusBadge
                                status={user.userType ? "active" : "inactive"}
                              />
                            </td>
                            <td>{user.age}</td>
                            {(userData.role === "admin" ||
                              hasPermission(permissions, "appUsers", "GET")) && (
                                <td>
                                  <ActionIcon
                                    type="view"
                                    onClick={() => {
                                      if (user.userType) {
                                        navigate(`/view-user/${user.userId}`)
                                      }
                                    }}
                                    tooltip="View User"
                                    disabled={!user.userType}
                                  />
                                </td>
                              )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </div>
            )}

          <StandardPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Col>
      </Row>
    </>
  )
}

export default Under18Users
